import { useDatePickerState } from '@react-stately/datepicker'
import * as Popover from '@radix-ui/react-popover'
import { AriaDatePickerProps, useDatePicker } from '@react-aria/datepicker'
import { FC, useMemo, useRef } from 'react'
import styles from 'components/atoms/inputs/DatePicker/DatePicker.styl'
import { DateField } from 'components/atoms/inputs/DatePicker/DateField'
import { Calendar } from 'components/atoms/inputs/DatePicker/Calendar/Calendar'
import { CalendarDate, DateValue } from '@internationalized/date'
import Icon from 'components/atoms/Icon'
import Button from 'components/features/pfm/Button/Button'

type DatePickerProps =
  & AriaDatePickerProps<DateValue>
  & {
    showCalendar?: boolean
    highlightedDates?: CalendarDate[]
    onChange?: (date: DateValue) => void
    onTodayClick?: () => void
  }

/**
 * Uses @react-aria for Input Field & Calendar management.
 * Uses @radix-ui Popover functionality
 */
export const DatePicker: FC<DatePickerProps> = (props) => {
  const { showCalendar, highlightedDates, onTodayClick, ...otherProps } = props
  const state = useDatePickerState(otherProps)
  const ref = useRef(null)

  const {
    groupProps,
    labelProps,
    fieldProps,
    calendarProps,
  } = useDatePicker(otherProps, state, ref)

  const calendarChild = useMemo(() => (
    <Calendar { ...calendarProps } highlightedDates={ highlightedDates } onTodayClick={ onTodayClick } />
  ), [calendarProps, highlightedDates, onTodayClick])

  const labelChild = useMemo(() => otherProps.label && (
    <label { ...labelProps }>{ otherProps.label }</label>
  ), [labelProps, otherProps])

  const popoverContent = useMemo(() => !showCalendar && (
    <Popover.Content sideOffset={ 5 } className={ styles.content }>
      { calendarChild }
      <Popover.Arrow className={ styles.arrow } />
      <Popover.Close aria-label='Close' className={ styles.close }>
        <Icon kind='close' />
      </Popover.Close>
    </Popover.Content>
  ), [calendarChild, showCalendar])

  if (!!showCalendar) {
    return (
      <fieldset className={ styles.datePicker }>
        { labelChild }
        <div { ...groupProps } ref={ ref } className={ styles.dateField }>
          <DateField { ...fieldProps } />
        </div>
        <br />
        { calendarChild }
      </fieldset>
    )
  }

  return (
    <Popover.Root>
      { labelChild}
      <div { ...groupProps } ref={ ref } className={ styles.dateField }>
        <DateField { ...fieldProps } />
        <Popover.Trigger asChild>
          <Button text='🗓' aria-label='Update dimensions' />
        </Popover.Trigger>
      </div>
      { popoverContent}
    </Popover.Root>
  )
}
